import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { getStaticContent } from '@/actions/ecom/staticContent';
import PageLayout from '@/components/Layouts/PageLayout';
import useLayoutRenderer from '@/hooks/useLayoutRenderer';
import SharedStaticContent from '@/components/Static/Shared/SharedStaticContent';
import { FC, MemoExoticComponent } from 'react';
import StaticImageBanner from '@/components/Static/Shared/StaticImageBanner';
import Heading from '@/shared/Heading';
import Div from '@/shared/Div';
import Container from '@/shared/Container';

import { CMS_COMPONENTS } from '@/constants/cms';
import DynamicPlaceholder from '@/shared/crisp/DynamicPlaceholder';
import classNames from 'classnames';
import TitleBanner from '@/shared/TitleBanner';
import LeftArrowIcon from '@/components/Icons/LeftArrow';
import Link from '@/shared/Link';
import SingleImagesBanner from '@/shared/crisp/SingleImagesBanner';
import { useAppConfigContext } from '@/context/AppContext';
import dlv from 'dlv';
import Map from '@/components/Map';
import Image from '@/shared/Image';
interface PageProps {
  page: {
    layout: any;
    seo: any;
    title: any;
    config: any;
  };
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  try {
    const page = await getStaticContent('/investor-relations/leadership-team');
    return {
      props: {
        page,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

const STATIC_MAP: { [key: string]: MemoExoticComponent<any> | FC<any> } = {
  [CMS_COMPONENTS.SHARED.TITLE_BANNER]: TitleBanner,
  [CMS_COMPONENTS.SHARED.HTML]: SharedStaticContent,
  [CMS_COMPONENTS.STATIC.IMAGE_BANNER]: StaticImageBanner,
  [CMS_COMPONENTS.SHARED.DYNAMIC_PLACEHOLDER]: DynamicPlaceholder,
  [CMS_COMPONENTS.BANNERS.SINGE_IMAGE]: SingleImagesBanner,
};

const LeadershipTeamPage: FC<PageProps> = ({ page }) => {
  const { layout, title, config } = page;

  const { state: appConfig } = useAppConfigContext();
  const renderLayout = useLayoutRenderer(layout || [], STATIC_MAP);
  const investorBase = '/investor-relations';
  const { dskImage, mobImage } = dlv(appConfig, 'config.investor-relations', {});
  const renderLeader = (leader: any, index: number) => {
    const { name, designation, image } = leader;
    return (
      <Div key={index} className="w-full text-cti-primary">
        <Image src={image} alt={name} className="w-full h-auto mb-s5" />
        <Heading level={3} className="text-h-3  mb-s2">
          {name}
        </Heading>
        <Div className="text-body-lg ">{designation}</Div>
      </Div>
    );
  };

  return (
    <PageLayout seo={page?.seo} pageWrapperClassName="font-primary">
      <Div
        className={classNames('static-page-container font-primary', {
          'investor-subpage': true,
        })}
      >
        {dskImage && mobImage ? (
          <SingleImagesBanner
            mobileImage={{
              data: {
                attributes: {
                  url: mobImage,
                },
              },
            }}
            desktopImage={{
              data: {
                attributes: {
                  url: dskImage,
                },
              },
            }}
          />
        ) : null}

        {title ? (
          <Container>
            <Div className="back-arrow-container my-s6">
              <Link href={investorBase} className="back-arrow-link">
                <LeftArrowIcon />
              </Link>
              <Heading level={1} className="!text-h-2 md:!text-hd-2 text-cti-primary">
                {title}
              </Heading>
            </Div>
          </Container>
        ) : null}
        <Container className="pb-s10">
          <Map
            list={config?.list}
            className="grid grid-cols-1 md:grid-cols-3 gap-s7"
            render={renderLeader}
          />
          {renderLayout}
        </Container>
      </Div>
    </PageLayout>
  );
};

export default LeadershipTeamPage;
